import { FormControl, Grid } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import dayjs from "dayjs";
import AsyncSearch from "../../components/asyncSearch/AsyncSearch";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICategory } from "../../interfaces/ICategory";
import { IExhibition } from "../../interfaces/IExhibition";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IJudgment } from "../../interfaces/IJudgment";
import { IJudgmentType } from "../../interfaces/IJudgmentType";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";
import { JudgmentCriteriaList } from "./judgmentCriteriaList/JudgmentCriteriaList";

export const Judgment: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();
  const timeout = useRef<any>(0);

  const [judgmentId, setJudgmentId] = useState("");
  const [exhibitionId] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("exhibitionId") || "";
  });

  const [exhibitionOptions, setExhibitionOptions] = useState<IOption[]>([]);
  const [exhibitionOption, setExhibitionOption] = useState<IOption | null>(
    null,
  );

  const [judgmentTypesOpions, setJudgmentTypesOptions] = useState<IOption[]>(
    [],
  );
  const [judgmentTypeOption, setJudgmentTypeOption] = useState<IOption | null>(
    null,
  );

  const [loadingExhibitions, setLoadingExhibitions] = useState(false);
  const [loadingJudgmentTypes, setLoadingJudgmentTypes] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState<IOption[]>([]);
  const [categoryOption, setCategoryOption] = useState<IOption | null>(null);
  const [parentCategoryOption, setParentCategoryOption] =
    useState<IOption | null>(null);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [finished, setFinished] = useState(true);

  const [registrationDisabled, setRegistrationDisabled] = useState(false);

  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const handleGoBack = useCallback(() => {
    navigate(`/exhibition/${exhibitionId}`);
  }, [navigate, exhibitionId]);

  const exhibitionToOption = (exhibition: IExhibition): IOption => {
    return {
      value: `${exhibition.id}`,
      description: `${exhibition.name} - ${dayjs(exhibition.date).format(
        "DD/MM/YYYY",
      )}`,
    };
  };

  const categoryToOption = (category: ICategory): IOption => {
    return {
      value: `${category.id}`,
      description: `${category.name}`,
    };
  };

  const judgmentTypeToOption = (judgmentTypes: IJudgmentType): IOption => {
    return {
      value: `${judgmentTypes.id}`,
      description: `${judgmentTypes.id} - ${judgmentTypes.name}`,
    };
  };

  const handleSetExhibition = (option: IOption | null) => {
    if (option) {
      setExhibitionOption(option);
      const selected = exhibitionOptions.find(exhibitionOption => {
        return exhibitionOption.value === option.value;
      });
      if (selected?.value) {
        setExhibitionOption(selected);
      } else {
        setExhibitionOption(null);
      }
    }
  };

  const handleSetJudgmentType = (option: IOption | null) => {
    if (option) {
      setCategoryOption(null);
      const selected = judgmentTypesOpions.find(judgmentTypes => {
        return judgmentTypes.value === option.value;
      });

      if (selected?.value) {
        setJudgmentTypeOption(selected);
      } else {
        setJudgmentTypeOption(null);
      }
    }
  };

  const handleSetCategory = (option: IOption | null) => {
    if (option) {
      setCategoryOption(option);
      const selected = categoryOptions.find(categoryOption => {
        return categoryOption.value === option.value;
      });
      if (selected?.value) {
        setCategoryOption(selected);
      } else {
        setCategoryOption(null);
      }
    }
  };

  const handleSetParentCategory = (option: IOption | null) => {
    if (option) {
      setParentCategoryOption(option);
      const selected = categoryOptions.find(categoryOption => {
        return categoryOption.value === option.value;
      });
      if (selected?.value) {
        setParentCategoryOption(selected);
      } else {
        setParentCategoryOption(null);
      }
    }
  };

  const handleResquestJudgmentTypes = useCallback(async () => {
    setLoadingJudgmentTypes(true);

    await api
      .get(
        `/judgment-types?perPage=100&currentPage=1&orderBy=id&orderDirection=asc`,
      )
      .then(response => {
        const judgmentTypes: IJudgmentType[] = response.data.data;

        const judgmentTypesOptions = judgmentTypes.map(judgmentTypeToOption);
        setJudgmentTypesOptions(judgmentTypesOptions);

        const selected = judgmentTypesOptions.find(judgmentTypes => {
          return judgmentTypes.value === judgmentTypeOption?.value;
        });

        if (selected?.value) {
          setJudgmentTypeOption(selected);
        } else {
          setJudgmentTypeOption(judgmentTypesOptions[0]);
        }
      })
      .catch(error => {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }

        toastr.error(
          error.response.data?.message || "Contate a equipe de suporte",
        );
      })
      .finally(() => {
        setLoadingJudgmentTypes(false);
      });
  }, [judgmentTypeOption?.value, navigate, signOut, toastr]);

  const handleListExhibitions = useCallback(
    async (filter = "") => {
      setLoadingExhibitions(true);

      await api
        .get(
          `/exhibitions/dynamically?perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}`,
        )
        .then(result => {
          if (result) {
            const options: IOption[] = result.data.data.map(exhibitionToOption);
            setExhibitionOptions(options);

            const params = new URLSearchParams(location.search);
            const exhibitionId = params.get("exhibitionId") || "";

            const selectedExhibition = options.find(
              exhibitionOption => exhibitionOption.value === exhibitionId,
            );

            if (selectedExhibition) {
              setExhibitionOption(selectedExhibition);
            } else {
              setExhibitionOption(null);
            }
          } else {
            setExhibitionOptions([]);
          }
        })
        .catch(error => {
          setExhibitionOptions([]);
          toastr.error(error.message);
        })
        .finally(() => {
          setLoadingExhibitions(false);
        });
    },
    [location.search, toastr],
  );

  const handleListCategories = useCallback(
    async (filter = "") => {
      if (!judgmentTypeOption) {
        toastr.error("Selecione um tipo de julgamento");
        return;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoadingCategories(true);

        await api
          .get(
            `/categories/dynamically/${judgmentTypeOption?.value}?perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}&exhibitionId=${exhibitionId}`,
          )
          .then(result => {
            if (result) {
              const options: IOption[] = result.data.data.map(categoryToOption);

              setCategoryOptions(options);
            } else {
              setCategoryOptions([]);
            }
          })
          .catch(error => {
            setCategoryOptions([]);
            toastr.error(error.message);
          })
          .finally(() => {
            setLoadingCategories(false);
          });
      }, timeout.current);
    },
    [exhibitionId, judgmentTypeOption, toastr],
  );

  const handleListParentCategories = async (filter = "") => {
    if (!judgmentTypeOption) {
      toastr.error("Selecione um tipo de julgamento");
      return;
    }

    clearInterval(timeout.current);
    timeout.current = setTimeout(async () => {
      setLoadingCategories(true);

      await api
        .get(
          `/categories/dynamically/${judgmentTypeOption?.value}?perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}`,
        )
        .then(result => {
          if (result) {
            const options: IOption[] = result.data.data.map(categoryToOption);

            setCategoryOptions(options);
          } else {
            setCategoryOptions([]);
          }
        })
        .catch(error => {
          setCategoryOptions([]);
          toastr.error(error.message);
        })
        .finally(() => {
          setLoadingCategories(false);
        });
    }, timeout.current);
  };

  const handleListExhibitionsAsync = async (filter = "") => {
    clearInterval(timeout.current);
    timeout.current = setTimeout(async () => {
      handleListExhibitions(filter);
    }, timeout.current);
  };

  const handleListJudgment = useCallback(async () => {
    const id = location.pathname.replace("/judgment", "").replace("/", "");

    await handleListExhibitions();
    await handleResquestJudgmentTypes();

    if (id) {
      setJudgmentId(id);
      setLoading(true);

      await api
        .get(`/judgments/${id}`)
        .then(response => {
          const judgment: IJudgment = response.data;

          if (judgment.exhibition) {
            const exhibitionOption = exhibitionToOption(judgment.exhibition);

            exhibitionOption && setExhibitionOption(exhibitionOption);
          }

          if (judgment.category) {
            const categoryOption = categoryToOption(judgment.category);

            categoryOption && setCategoryOption(categoryOption);
          }

          if (judgment.judgmentType) {
            const judgmentTypeOption = judgmentTypeToOption(
              judgment.judgmentType,
            );

            judgmentTypeOption && setJudgmentTypeOption(judgmentTypeOption);
          }

          setFinished(!!judgment.finished);

          setRegistrationDisabled(!!judgment.finished);
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    navigate,
    signOut,
    toastr,
    location.pathname,
    handleListExhibitions,
    handleResquestJudgmentTypes,
  ]);

  const handleDeleteJudgment = useCallback(async () => {
    const id = location.pathname.replace("/judgment", "").replace("/", "");

    if (id) {
      setLoading(true);

      await api
        .delete(`/judgments/${id}`)
        .then(() => {
          handleGoBack();
          toastr.success("Julgamento excluído com sucesso");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, handleGoBack, signOut, toastr, location.pathname]);

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});
      setLoading(true);

      const schema = Yup.object().shape({
        judgmentTypeId: Yup.number()
          .required("Tipo de julgamento é obrigatório")
          .positive("Tipo de julgamento é obrigatório"),
        exhibitionId: Yup.string().uuid().required("Exposição é obrigatória"),
        categoryId: Yup.string().uuid().required("Exposição é obrigatória"),
      });

      if (judgmentId) {
        const data: IJudgment = {
          judgmentTypeId: judgmentTypeOption?.value
            ? Number(judgmentTypeOption?.value)
            : 0,
          exhibitionId: exhibitionOption?.value || "",
          categoryId: categoryOption?.value || "",
          parentCategoryId: parentCategoryOption?.value || null,
          finished,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/judgments/${judgmentId}`, data)
          .then(async () => {
            await handleListJudgment();
            toastr.success("Julgamento atualizada com sucesso");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const data: IJudgment = {
          judgmentTypeId: judgmentTypeOption?.value
            ? Number(judgmentTypeOption?.value)
            : 0,
          exhibitionId: exhibitionOption?.value || "",
          categoryId: categoryOption?.value || "",
          parentCategoryId: parentCategoryOption?.value || null,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/judgments`, data)
          .then(response => {
            toastr.success("Julgamento cadastrado com sucesso");
            setJudgmentId(response.data.id);
            handleGoBack();
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(
        error.response.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [
    judgmentId,
    exhibitionOption,
    categoryOption,
    finished,
    judgmentTypeOption,
    parentCategoryOption,
    handleGoBack,
    navigate,
    signOut,
    toastr,
    handleListJudgment,
  ]);

  useEffect(() => {
    handleListJudgment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Julgamento</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                size="small"
                error={!!formErrors.exhibitionId}
                sx={{ width: "100%" }}
                required
              >
                <AsyncSearch
                  options={exhibitionOptions}
                  setOptions={setExhibitionOptions}
                  option={exhibitionOption}
                  setOption={handleSetExhibition}
                  asyncSearch={handleListExhibitionsAsync}
                  loading={loadingExhibitions}
                  error={Boolean(formErrors?.exhibitionId)}
                  errorMessage={formErrors?.exhibitionId || null}
                  label="Exposição"
                  required
                  disabled
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                size="small"
                error={!!formErrors.exhibitionId}
                sx={{ width: "100%" }}
                required
              >
                <AsyncSearch
                  options={judgmentTypesOpions}
                  setOptions={setJudgmentTypesOptions}
                  option={judgmentTypeOption}
                  setOption={handleSetJudgmentType}
                  asyncSearch={handleListJudgment}
                  loading={loadingJudgmentTypes}
                  error={Boolean(formErrors?.judgmentTypeId)}
                  errorMessage={formErrors?.judgmentTypeId || null}
                  label="Tipo de Julgamento"
                  required
                  disabled={!!judgmentId}
                  autofocus
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                size="small"
                error={!!formErrors.categoryId}
                sx={{ width: "100%" }}
                required
              >
                <AsyncSearch
                  options={categoryOptions}
                  setOptions={setCategoryOptions}
                  option={parentCategoryOption}
                  setOption={handleSetParentCategory}
                  asyncSearch={handleListParentCategories}
                  loading={loadingCategories}
                  error={Boolean(formErrors?.parentCategoryId)}
                  errorMessage={formErrors?.parentCategoryId || null}
                  label="Categoria Pai"
                  disabled={!judgmentTypeOption || !!judgmentId}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                size="small"
                error={!!formErrors.categoryId}
                sx={{ width: "100%" }}
                required
              >
                <AsyncSearch
                  options={categoryOptions}
                  setOptions={setCategoryOptions}
                  option={categoryOption}
                  setOption={handleSetCategory}
                  asyncSearch={handleListCategories}
                  loading={loadingCategories}
                  error={Boolean(formErrors?.categoryId)}
                  errorMessage={formErrors?.categoryId || null}
                  label="Categoria"
                  required
                  disabled={!judgmentTypeOption || !!judgmentId}
                />
              </FormControl>
            </Grid>
          </Grid>
          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={handleGoBack}
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            {judgmentId && !registrationDisabled && (
              <ButtonTheme
                onClick={() => setDialogDeleteOpen(true)}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Excluir
              </ButtonTheme>
            )}

            <ButtonTheme
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              Confirmar
            </ButtonTheme>
          </ButtonGroup>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={judgmentId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${judgmentId}`}
          handleConfirmation={handleDeleteJudgment}
          deny={() => setDialogDeleteOpen(false)}
        />

        {loading && <BackdropCustom />}
      </PageCard>

      {judgmentId && judgmentTypeOption && (
        <JudgmentCriteriaList
          judgmentId={judgmentId}
          judgmentTypeId={Number(judgmentTypeOption.value)}
          registrationDisabled={registrationDisabled}
        />
      )}
    </>
  );
};
